'use client'

import { Bus, BusFront, Car, ChevronDown, Truck, Van } from 'lucide-react'
import { useEffect, useState } from 'react'
import { storageKeys } from '@/lib/storage-keys'
import type { Vehicle, VehicleId } from '@/lib/types'
import { VEHICLES } from '@/lib/vehicles'
import { useRouteStore } from '@/stores/route-store'
import { SemiTruck } from './icons/semi-truck'

// Icon per vehicle (referenced by string name in VEHICLES). Centralized so a
// single edit swaps an icon everywhere. Tuned for recognition at 16px:
//   Car · Bus (15-pax) · Van (Sprinter) · Truck (box) · BusFront (coach) ·
//   SemiTruck (custom tractor-trailer — lucide has no proper 18-wheeler).
const ICON_MAP: Record<string, React.ElementType> = {
	Car,
	Bus,
	BusFront,
	Van,
	Truck,
	SemiTruck,
}

/** Read a saved MPG override for one vehicle from localStorage (1–50 valid). */
function readSavedMpg(id: VehicleId): number | null {
	if (typeof window === 'undefined') return null
	try {
		const raw = window.localStorage.getItem(storageKeys.customMpg(id))
		const parsed = raw === null ? null : Number.parseFloat(raw)
		return parsed !== null && Number.isFinite(parsed) && parsed >= 1 && parsed <= 50 ? parsed : null
	} catch {
		return null
	}
}

type MpgMap = Partial<Record<VehicleId, number | null>>

/**
 * Per-vehicle MPG overrides from localStorage so every dropdown option shows
 * its own saved value — not just the selected one — and the currently selected
 * vehicle stays live-synced with the store as the user edits its override.
 *
 * SSR-safe: empty map on first render (server + client agree → no hydration
 * mismatch), populated after mount.
 */
function useVehicleMpgOverrides(): MpgMap {
	const currentVehicleId = useRouteStore((s) => s.vehicleId)
	const currentMpg = useRouteStore((s) => s.customMpg)
	const [overrides, setOverrides] = useState<MpgMap>({})

	useEffect(() => {
		if (typeof window === 'undefined') return
		const loaded: MpgMap = {}
		for (const v of VEHICLES) loaded[v.id] = readSavedMpg(v.id)
		setOverrides(loaded)
	}, [])

	useEffect(() => {
		setOverrides((prev) => ({ ...prev, [currentVehicleId]: currentMpg }))
	}, [currentVehicleId, currentMpg])

	return overrides
}

/** "Tour Bus · 7 MPG · diesel" — appends "(custom)" when an MPG override is set. */
function optionLabel(vehicle: Vehicle, savedMpg: number | null | undefined): string {
	const mpg = savedMpg ?? vehicle.baseMpg
	const custom = savedMpg != null ? ' (custom)' : ''
	return `${vehicle.name} · ${mpg} MPG${custom} · ${vehicle.defaultFuel}`
}

interface VehiclePickerProps {
	value: VehicleId
	onChange: (v: VehicleId) => void
	/** Kept for call-site compatibility; per-vehicle overrides are read internally. */
	customMpg: number | null
}

/**
 * Vehicle selector — a styled native `<select>`. A native control gives the OS
 * picker wheel on mobile (the best small-screen UX) and full keyboard/a11y for
 * free, while taking a fraction of the vertical space the old card grid did.
 * The selected vehicle's icon sits in the trigger; each option carries its
 * name, effective MPG (incl. any saved override), and fuel type.
 */
export function VehiclePicker({ value, onChange }: VehiclePickerProps) {
	const overrides = useVehicleMpgOverrides()
	const selectedIconName = VEHICLES.find((v) => v.id === value)?.icon
	const SelectedIcon = ICON_MAP[selectedIconName ?? ''] ?? Truck

	return (
		<div>
			<label htmlFor="vehicle-type" className="mb-2 block text-xs font-medium text-text-secondary">
				Vehicle Type
			</label>
			<div className="relative">
				<SelectedIcon
					className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-brand"
					aria-hidden
				/>
				<select
					id="vehicle-type"
					value={value}
					onChange={(e) => onChange(e.target.value as VehicleId)}
					className="w-full cursor-pointer appearance-none rounded-md border border-border bg-surface py-2 pl-9 pr-9 text-sm text-text-primary [color-scheme:dark] transition-colors hover:bg-surface-hover focus:border-brand focus:outline-none"
				>
					{VEHICLES.map((v) => (
						<option key={v.id} value={v.id}>
							{optionLabel(v, overrides[v.id])}
						</option>
					))}
				</select>
				<ChevronDown
					className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-text-muted"
					aria-hidden
				/>
			</div>
		</div>
	)
}
